export type ShopPerformanceFilters = {
	dateFrom: string;
	dateTo: string;
	methods: string[];
	buildings: string[];
	materials: string[];
	finishes: string[];
	coatings: string[];
	leadTimeMin?: number;
	leadTimeMax?: number;
	qtyMin?: number;
	qtyMax?: number;
};

const splitList = (raw: string | null): string[] =>
	raw ? raw.split(',').map((v) => v.trim()).filter(Boolean) : [];

const parseIntParam = (raw: string | null): number | undefined => {
	if (raw === null || raw === '') return undefined;
	const n = Number(raw);
	return Number.isInteger(n) ? n : undefined;
};

/** Query string for the report API. Empty lists are omitted (all). */
export function buildReportQuery(filters: ShopPerformanceFilters): string {
	const params = new URLSearchParams();
	params.set('date_from', filters.dateFrom);
	params.set('date_to', filters.dateTo);
	if (filters.methods.length) params.set('methods', filters.methods.join(','));
	if (filters.buildings.length) params.set('buildings', filters.buildings.join(','));
	if (filters.materials.length) params.set('materials', filters.materials.join(','));
	if (filters.finishes.length) params.set('finishes', filters.finishes.join(','));
	if (filters.coatings.length) params.set('coatings', filters.coatings.join(','));
	if (filters.leadTimeMin !== undefined) {
		params.set('lead_time_min', String(filters.leadTimeMin));
	}
	if (filters.leadTimeMax !== undefined) {
		params.set('lead_time_max', String(filters.leadTimeMax));
	}
	if (filters.qtyMin !== undefined) params.set('qty_min', String(filters.qtyMin));
	if (filters.qtyMax !== undefined) params.set('qty_max', String(filters.qtyMax));
	return params.toString();
}

export function filtersFromSearchParams(
	search: URLSearchParams,
	fallback: ShopPerformanceFilters
): ShopPerformanceFilters {
	const dateFrom = search.get('date_from') || fallback.dateFrom;
	const dateTo = search.get('date_to') || fallback.dateTo;
	const next: ShopPerformanceFilters = {
		dateFrom,
		dateTo,
		methods: splitList(search.get('methods')),
		buildings: splitList(search.get('buildings')),
		materials: splitList(search.get('materials')),
		finishes: splitList(search.get('finishes')),
		coatings: splitList(search.get('coatings')),
		leadTimeMin: parseIntParam(search.get('lead_time_min')),
		leadTimeMax: parseIntParam(search.get('lead_time_max')),
		qtyMin: parseIntParam(search.get('qty_min')),
		qtyMax: parseIntParam(search.get('qty_max')),
	};
	return next;
}

export function filtersToSearchParams(filters: ShopPerformanceFilters): URLSearchParams {
	return new URLSearchParams(buildReportQuery(filters));
}

export function filtersEqual(a: ShopPerformanceFilters, b: ShopPerformanceFilters): boolean {
	return buildReportQuery(a) === buildReportQuery(b);
}

const MAX_RANGE_DAYS = 366;

/** True when the inclusive YYYY-MM-DD window is inverted or over 366 days. */
export function dateWindowInvalid(dateFrom: string, dateTo: string): boolean {
	if (!dateFrom || !dateTo || dateTo < dateFrom) return true;
	const from = Date.parse(`${dateFrom}T00:00:00Z`);
	const to = Date.parse(`${dateTo}T00:00:00Z`);
	if (Number.isNaN(from) || Number.isNaN(to)) return true;
	const days = Math.round((to - from) / 86400000) + 1;
	return days > MAX_RANGE_DAYS;
}

/** True when min is set above max. Blank on either side is not an error. */
export function invertedRange(min?: number, max?: number): boolean {
	return min !== undefined && max !== undefined && min > max;
}
